// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the user's own wallpaper for the glass theme. Too big for the
// settings snapshot, so it lives as one blob in IndexedDB on this device and
// is exposed as an object URL the backdrop layer can paint.

import {getProtectedIndexedDB} from '@app/features/platform/state/ProtectedWebStorage';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('GlassWallpaperImage');
const DB_NAME = 'konvo-glass';
const STORE = 'wallpaper';
const KEY = 'image';
/** Longest edge after downscaling; enough for a 4K display once blurred. */
export const WALLPAPER_MAX_EDGE = 2560;
export const WALLPAPER_MAX_INPUT_BYTES = 25 * 1024 * 1024;
const JPEG_QUALITY = 0.86;

function openDatabase(): Promise<IDBDatabase | null> {
	const factory = getProtectedIndexedDB();
	if (!factory) return Promise.resolve(null);
	return new Promise((resolve) => {
		try {
			const request = factory.open(DB_NAME, 1);
			request.onupgradeneeded = () => {
				if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
			};
			request.onsuccess = () => resolve(request.result);
			request.onerror = () => resolve(null);
			request.onblocked = () => resolve(null);
		} catch {
			resolve(null);
		}
	});
}

async function withStore<T>(
	mode: IDBTransactionMode,
	run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
	const db = await openDatabase();
	if (!db) return null;
	return new Promise((resolve) => {
		try {
			const tx = db.transaction(STORE, mode);
			const request = run(tx.objectStore(STORE));
			request.onsuccess = () => resolve(request.result ?? null);
			request.onerror = () => resolve(null);
			tx.oncomplete = () => db.close();
			tx.onabort = () => {
				db.close();
				resolve(null);
			};
		} catch {
			db.close();
			resolve(null);
		}
	});
}

/** Shrink a picked image so the stored blob stays small; falls back to the original on failure. */
export async function downscaleImage(file: Blob): Promise<Blob> {
	try {
		const bitmap = await createImageBitmap(file);
		const scale = Math.min(1, WALLPAPER_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
		const width = Math.max(1, Math.round(bitmap.width * scale));
		const height = Math.max(1, Math.round(bitmap.height * scale));
		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const context = canvas.getContext('2d');
		if (!context) return file;
		context.drawImage(bitmap, 0, 0, width, height);
		bitmap.close();
		const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
		return blob ?? file;
	} catch {
		return file;
	}
}

export type GlassWallpaperImageError = 'unsupported' | 'too_large' | 'storage';

class GlassWallpaperImage {
	url: string | null = null;
	loading = true;
	saving = false;
	error: GlassWallpaperImageError | null = null;
	/** Stored size in bytes, for the settings row. */
	size = 0;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.load();
	}

	get hasImage(): boolean {
		return this.url != null;
	}

	async load(): Promise<void> {
		const blob = await withStore<Blob | undefined>(
			'readonly',
			(store) => store.get(KEY) as IDBRequest<Blob | undefined>,
		);
		runInAction(() => {
			this.replaceUrl(blob instanceof Blob ? blob : null);
			this.loading = false;
		});
	}

	async setImage(file: File): Promise<boolean> {
		if (!file.type.startsWith('image/')) {
			runInAction(() => {
				this.error = 'unsupported';
			});
			return false;
		}
		if (file.size > WALLPAPER_MAX_INPUT_BYTES) {
			runInAction(() => {
				this.error = 'too_large';
			});
			return false;
		}
		runInAction(() => {
			this.saving = true;
			this.error = null;
		});
		const blob = await downscaleImage(file);
		const stored = await withStore('readwrite', (store) => store.put(blob, KEY));
		runInAction(() => {
			this.saving = false;
			if (stored === null && !getProtectedIndexedDB()) {
				this.error = 'storage';
				return;
			}
			this.replaceUrl(blob);
		});
		if (this.error) logger.warn('KONVO glass: wallpaper image could not be stored');
		return this.error == null;
	}

	async clear(): Promise<void> {
		await withStore('readwrite', (store) => store.delete(KEY));
		runInAction(() => {
			this.replaceUrl(null);
			this.error = null;
		});
	}

	private replaceUrl(blob: Blob | null): void {
		if (this.url) URL.revokeObjectURL(this.url);
		this.url = blob ? URL.createObjectURL(blob) : null;
		this.size = blob?.size ?? 0;
	}
}

export default new GlassWallpaperImage();
